import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../Styles/PageStyles";
import Images from "../../Images";
import tw from "tailwind-styled-components";
import moment from "moment";
import Config, { PaginationLimit } from "../../Config";
import { useNavigate } from "react-router-dom";
import Tooltip from "../Tooltip";
import { IoMdEye } from "react-icons/io";
import { capitalizeFirstLetter, handleCopy, truncateText } from "../../helpers";
import { IoCopySharp } from 'react-icons/io5';

const Table = ({
    apiData,
    setSelectedData,
    userType,
    isMovingOut,
    handleOpenTenantModal,
    setIsViewDetails,
    handleAssignProperty
}) => {
    const storageKey = `booking_active_page`;
    const [data, setData] = useState([
        {
            deviceName: "",
            extractionTime: "",
            processingTime: "",
            drops: "",
            action: "",
        },
    ]);
    const navigate = useNavigate();
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [previousId, setPreviousId] = useState(null);
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const toggleRow = (index) => {
        setSelectedRow(selectedRow === index ? null : index);
    };

    // Load saved page index
    const storageValue = sessionStorage.getItem(storageKey);
    const savedPage = storageValue !== null ? Number(storageValue) : 0;
    const [currentPage, setCurrentPage] = useState(savedPage);
    useEffect(() => {
        setCurrentPage(savedPage);
    }, [savedPage]);
    const viewDetails = (item) => {
        setIsViewDetails(true);
        setSelectedData(item);
        setOpenActionMenu(null);
    };
    const ActionBtns = (item) => (
        <Tooltip label="View details">
            <button
                onClick={() => viewDetails(item)}
                className="p-2 rounded-full text-gray-400 hover:bg-gray-100 transition"
            >
                <IoMdEye size={18} />
            </button>
        </Tooltip>
    );


    useEffect(() => {
        if (apiData) {
            const tempData = apiData.map((item) => ({
                name: getName(item),
                property: getPropertyName(item),
                bookingNo: item.applicationNumber ?? "-",
                dues: getDues(item),
                moveInDate: moveInDate(item),
                status: getStatus(item.status),
                action: ActionBtns(item),
            }));
            setData(tempData);
        }
    }, [apiData, openActionMenu]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                !event.target.closest("#menuOptions") &&
                !event.target.closest(".action-btn")
            ) {
                setOpenActionMenu(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const getName = (item) => {
        return <div
            className="ml-2 flex items-start gap-3"
        >
            {/* Name + Mobile */}
            <div className="flex flex-col min-w-0">
                <span className="font-medium text-gray-700 truncate"
                >
                    {capitalizeFirstLetter(truncateText(item?.tenantName, 25))}
                </span>

                {item?.tenantMobile && (
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                        <span>{'#' + item?.tenantMobile}</span>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(item?.tenantMobile);
                            }}
                            className="hover:text-gray-600 transition"
                            title="Copy number"
                        >
                            <IoCopySharp size={12} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    };


    const getStatus = (status) => {
        switch (status) {
            case Config.BOOKING_STATUS.LINK_SENT:
                return <LinkSent>Link Sent</LinkSent>;

            case Config.BOOKING_STATUS.FORM_SUBMITTED:
                return <Submitted>Submitted</Submitted>;

            case Config.BOOKING_STATUS.PAYMENT_DONE:
                return <PaymentDone>Payment Done</PaymentDone>;

            case Config.BOOKING_STATUS.CONFIRMED:
                return <Confirmed>Confirmed</Confirmed>;

            case Config.BOOKING_STATUS.CANCELLED:
                return <Cancelled>Cancelled</Cancelled>;
            case Config.BOOKING_STATUS.MOVED_IN:
                return <MovedIn>Moved In</MovedIn>;

            default:
                return null;
        }
    };


    const getDues = (item) => {
        if (Number(item?.totalDues) === 0) {
            return (
                <span className="text-xs text-green-600 bg-green-50 px-3 py-1 rounded-full">
                    No dues
                </span>
            );
        }

        return <Amount >₹{item?.totalDues || 0}</Amount>;
    };

    const getPropertyName = (item) => (
        <div className="flex flex-col gap-0.5">
            <span
                className="text-gray-600 text-sm truncate"
                title={item.propertyName}
            >
                {item.propertyName || "Not Assigned"}
            </span>

            {!item.propertyName ? (
                <button
                    type="button"
                    onClick={() => handleAssignProperty(item)}
                    className="text-xs text-blue-600 hover:text-blue-700 hover:underline text-left w-fit"
                >
                    Assign Property & Room
                </button>
            ) : (
                <span className="text-xs text-gray-400">
                    Room {item.roomName}
                </span>
            )
            }
        </div>
    );

    const moveInDate = (item) => {
        return (
            <div className="flex gap-3 ">
                <div className="flex flex-col text-sm text-gray-600">
                    <span>
                        {item.moveInDate
                            ? moment(item.moveInDate).format("DD, MMM YYYY")
                            : "-"}
                    </span>
                </div>
            </div>
        );
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const columns = React.useMemo(() => {
        if (isMobileView) {
            // Display only "User" column for mobile screens
            return [
                {
                    Header: "Booking",
                    accessor: "name",
                },
                {
                    Header: "Property",
                    accessor: "property",
                },
            ];
        }
        return [
            {
                Header: "Booking",
                accessor: "name",
            },
            {
                Header: "Booking No.",
                accessor: "bookingNo",
            },
            {
                Header: "Property",
                accessor: "property",
            },
            // {
            //     Header: "Dues",
            //     accessor: "dues",
            // },
            {
                Header: "Status",
                accessor: "status",
            },
            {
                Header: "",
                accessor: "action",
            },
        ];
    }, [isMobileView, isMovingOut]);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex, pageSize },
        previousPage,
        nextPage,
        setPageSize,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        sessionStorage.setItem(storageKey, pageIndex);
    }, [pageIndex, storageKey]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()} hasdata={(apiData?.length > 0).toString()}>
                    {page.map((row, rowIndex) => {
                        prepareRow(row);
                        const rowData = apiData[rowIndex];
                        return (
                            <React.Fragment key={rowIndex}>
                                <Tr
                                    {...row.getRowProps({ style: { height: "80px" } })}
                                    hasdata={(apiData?.length > 0).toString()}
                                >
                                    {row.cells.map((cell, cellIndex) => {
                                        const isActionCell = cell.column.id === "action";
                                        return (
                                            <Td
                                                {...cell.getCellProps()}
                                            // onClick={(event) => {
                                            //   // Prevent row click for action button
                                            //   if (isActionCell) event.stopPropagation();
                                            // }}
                                            // onClick={["name", "rent", "roomNum", "moveInOutDate", "status"].includes(cell.column.id) ? () => openTenantDetailsModal ('Tenant Info', cell) : null}

                                            >
                                                {cell.render("Cell")}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                                {selectedRow === rowIndex && window.innerWidth < 768 && (
                                    <Tr>
                                        <Td colSpan={columns.length}>
                                            <ul className="flex flex-col gap-2">
                                                <li>
                                                    <strong>Rent</strong>{" "}
                                                    {data[rowIndex].rent}
                                                </li>
                                                <li>
                                                    <strong>Agreement Duration</strong>{" "}
                                                    {data[rowIndex].moveInOutDate}
                                                </li>

                                                <li className="flex gap-2">
                                                    <strong>Status</strong>{" "}
                                                    {data[rowIndex].status}
                                                </li>
                                                <li>
                                                    <strong>Dues:</strong> {data[rowIndex].dues}
                                                </li>
                                            </ul>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>
            {apiData?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>

                    <div className="flex gap-1">
                        <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75 " />
                        </PrevBtn>
                        <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
            {/* {apiData?.length === 0 && <NotFoundModel />} */}
        </>
    );
};

const CustomTable = tw.table` w-full text-gray-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
// const Tr = tw.tr`${(props) =>
//   props.hasdata === "true"
//     ? "border-b"
//     : ""} rounded-md overflow-hidden hover:bg-slate-50`;
const Tr = tw.tr`
  border-b
  hover:bg-slate-50
  transition
`;

const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;

const Amount = tw.p`text-sm font-medium text-rose-700 bg-rose-100 px-3 py-1 rounded-full hover:bg-rose-200 transition inline-flex`


const LinkSent = tw.div`
  bg-gray-100 text-gray-700 border border-gray-200
  text-xs w-24 h-7 flex items-center justify-center rounded-lg
`;

const Submitted = tw.div`
  bg-amber-50 text-amber-700 border border-amber-100
  text-xs w-24 h-7 flex items-center justify-center rounded-lg
`;

const PaymentDone = tw.div`
  bg-blue-50 text-blue-700 border border-blue-100
  text-xs w-24 h-7 flex items-center justify-center rounded-lg
`;

const Confirmed = tw.div`
  bg-cyan-50 text-cyan-700 border border-cyan-100
  text-xs w-24 h-7 flex items-center justify-center rounded-lg
`;

const Cancelled = tw.div`
  bg-rose-50 text-rose-700 border border-rose-100
  text-xs w-24 h-7 flex items-center justify-center rounded-lg
`;
const MovedIn = tw.div`
  bg-green-50 text-green-700 border border-green-100
  text-xs w-24 h-7 flex items-center justify-center rounded-lg
`;

export default Table;
